import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchAdminProductCategoryFields } from '@/api/(admin)/product-categories';
import type { AdminProductCategoryField } from '@/api/(admin)/product-categories/types';
import {
  buildCategoryFieldsQs,
  CATEGORY_FIELDS_PAGE_SIZE,
} from '../-utils/listParams';

/** Prefix for every fields cache entry, scoped per category. */
export const adminCategoryFieldsQueryKey = [
  'admin',
  'product-category-fields',
] as const;

export function adminCategoryFieldsQueryOptions(categoryId: string) {
  return queryOptions({
    queryKey: [...adminCategoryFieldsQueryKey, categoryId] as const,
    queryFn: () =>
      fetchAdminProductCategoryFields(buildCategoryFieldsQs(categoryId)),
  });
}

export function useAdminCategoryFields(categoryId: string) {
  const query = useQuery(adminCategoryFieldsQueryOptions(categoryId));

  /**
   * Ordered by `idx`, because the list is an ordered one and the endpoint has
   * no `sort` param to do it server-side.
   */
  const rows: AdminProductCategoryField[] = [...(query.data?.data ?? [])].sort(
    (a, b) => (a.idx ?? 0) - (b.idx ?? 0),
  );

  const totalCount = query.data?.metadata.pagination.total ?? rows.length;

  /**
   * True when the category has more fields than one request returns.
   *
   * The list is unpaginated and `nextIdx` is derived from what came back, so a
   * truncated page would both hide fields and propose an `idx` already in use.
   * The page says so rather than pretending the short list is the whole list.
   */
  const isTruncated = totalCount > rows.length;

  /** Where a newly created field goes. `idx` is required on the create body. */
  const nextIdx =
    rows.length === 0 ? 0 : Math.max(...rows.map(r => r.idx ?? 0)) + 1;

  return {
    ...query,
    rows,
    totalCount,
    isTruncated,
    pageSize: CATEGORY_FIELDS_PAGE_SIZE,
    nextIdx,
  };
}
